import { makeStyles } from '@material-ui/core';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../auth';
import { fetchStagingDomainFallbackState } from '../../../Apis/apiOfManaged';
import { localStorageArrayHandler } from '../../../CommonFunction/managePermission';
import Footer from '../../../Footer/footer';
import various from '../../../General/various';

const targetLabels = {
  touzen: '刀禅',
  yowan: '揺腕法',
};
const STAGING_SWITCH_DEVELOPER_EMAIL = 'shimazaki@shimashou.com';

const canUseStagingSwitch = (user) =>
  String(user?.email || '').toLowerCase() === STAGING_SWITCH_DEVELOPER_EMAIL;

const useStyles = makeStyles((theme) => ({
  page: {
    margin: '80px',
    textAlign: 'start',
    [theme.breakpoints.down('sm')]: {
      margin: '48px 24px',
    },
  },
  header: {
    marginBottom: '32px',
    '& h1': {
      margin: '0 0 16px',
      fontSize: '1.4rem',
    },
    '& p': {
      margin: '0 0 8px',
      lineHeight: '1.9',
    },
  },
  panel: {
    marginBottom: '32px',
    padding: '24px',
    border: 'solid 1px rgba(0,0,0,0.12)',
    borderRadius: '8px',
    background: '#fff',
    boxShadow: '0px 0px 15px rgba(0,0,0,0.06)',
  },
  stateGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  stateField: {
    padding: '16px',
    border: 'solid 1px rgba(0,0,0,0.1)',
    borderRadius: '6px',
    background: 'rgba(0,0,0,0.02)',
    '& p': {
      margin: 0,
    },
  },
  stateLabel: {
    marginBottom: '8px !important',
    color: 'rgba(0,0,0,0.62)',
    fontSize: '0.85rem',
  },
  stateValue: {
    fontWeight: 700,
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '24px',
  },
  button: {
    minWidth: '150px',
    border: 'none',
    borderRadius: '0.5em',
    padding: '10px 16px',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    cursor: 'pointer',
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
  },
  secondaryButton: {
    backgroundColor: '#555',
  },
  message: {
    marginTop: '16px',
    padding: '12px 16px',
    borderLeft: `solid 4px ${theme.palette.primary.main}`,
    background: 'rgba(0,0,0,0.04)',
    lineHeight: '1.7',
  },
  notes: {
    '& li': {
      marginBottom: '8px',
      lineHeight: '1.8',
    },
  },
  backLink: {
    display: 'inline-block',
    marginTop: '16px',
  },
}));

const getTargetLabel = (target) => targetLabels[target] || '未設定';

const getEnabledLabel = (enabled) => {
  if (enabled === true) {
    return '有効';
  }

  if (enabled === false) {
    return '無効';
  }

  return '不明';
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};

  return responseData.detail || responseData.message || fallbackMessage;
};

const StateField = ({ classes, label, value }) => (
  <div className={classes.stateField}>
    <p className={classes.stateLabel}>{label}</p>
    <p className={classes.stateValue}>{value || '-'}</p>
  </div>
);

const StagingDomainFallback = () => {
  const { isAuth, user } = useContext(AuthContext);
  const permissions = localStorageArrayHandler('parse', various.permissionKey);
  const hasAdminPermission = permissions.includes('admin');
  const canShowStagingSwitch = canUseStagingSwitch(user);
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();

  const loadState = ({ resetMessage = true } = {}) => {
    setIsLoading(true);
    if (resetMessage) {
      setMessage('');
    }

    return fetchStagingDomainFallbackState()
      .then((payload) => {
        setState(payload);
        return payload;
      })
      .catch((error) => {
        setState(null);
        setMessage(getErrorMessage(error, 'Staging表示状態を読み込めませんでした。'));
        return null;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!isAuth || !hasAdminPermission || !canShowStagingSwitch) {
      setIsLoading(false);
      return;
    }

    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, hasAdminPermission, canShowStagingSwitch]);

  const allowedTargets = useMemo(() => {
    const source = state?.allowedTargets || ['touzen', 'yowan'];
    return source.filter((target) => target === 'touzen' || target === 'yowan');
  }, [state]);

  const handleAuth = () => {
    navigate(various.general.login, { state: { previous: location.pathname } });
  };

  if (!isAuth || !hasAdminPermission) {
    return (
      <>
        <div className={classes.page}>
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleAuth}
          >
            ログイン
          </span>
          してください。
        </div>
        <Footer />
      </>
    );
  }

  if (!canShowStagingSwitch) {
    return (
      <>
        <div className={classes.page}>
          <section className={classes.header}>
            <h1>Staging表示状態</h1>
            <p>このページは開発者専用です。</p>
            <Link className={classes.backLink} to={various.general.managed}>
              管理画面Topへ戻る
            </Link>
          </section>
        </div>
        <Footer />
      </>
    );
  }

  const currentTarget = state?.config?.target || '';
  const currentEnabled = state?.config?.enabled;

  const reloadCurrentPage = () => {
    window.location.reload();
  };

  const openHerokuappRoot = () => {
    window.location.assign('/');
  };

  return (
    <>
      <div className={classes.page}>
        <section className={classes.header}>
          <h1>Staging表示状態</h1>
          <p>
            Staging Herokuapp fallback の現在値を安全な読み取り専用APIで確認します。
          </p>
          <p>
            変更は1Password Appで行い、NodeV11のEnvkit V3 Staging dry-run/applyでHerokuへ同期します。
          </p>
          <Link className={classes.backLink} to={various.general.managed}>
            管理画面Topへ戻る
          </Link>
        </section>

        <section className={classes.panel}>
          <h2>現在の設定</h2>
          {isLoading ? <p>読み込み中...</p> : null}
          <div className={classes.stateGrid}>
            <StateField classes={classes} label="Heroku App" value={state?.appName} />
            <StateField classes={classes} label="API環境" value={state?.environment} />
            <StateField classes={classes} label="Fallback" value={getEnabledLabel(currentEnabled)} />
            <StateField classes={classes} label="表示対象" value={getTargetLabel(currentTarget)} />
            <StateField
              classes={classes}
              label="切替可能な対象"
              value={allowedTargets.map(getTargetLabel).join(' / ')}
            />
          </div>

          <div className={classes.actions}>
            <button
              type="button"
              className={`${classes.button} ${classes.secondaryButton}`}
              onClick={reloadCurrentPage}
            >
              現在のページを再読み込み
            </button>
            <button
              type="button"
              className={`${classes.button} ${classes.secondaryButton}`}
              onClick={openHerokuappRoot}
            >
              Herokuappトップを開き直す
            </button>
          </div>

          {message ? <p className={classes.message}>{message}</p> : null}
        </section>

        <section className={`${classes.panel} ${classes.notes}`}>
          <h2>注意事項</h2>
          <ul>
            <li>この画面からHeroku Config Varを変更することはできません。</li>
            <li>1Password Appで正本を更新し、NodeV11でEnvkit V3のStaging dry-runを確認してからchanged-only applyを実行してください。</li>
            <li>同期後の確認は、release/restart完了後にページを再読み込みしてください。</li>
            <li>本番環境では使用しません。実ドメイン接続後はhost-based routingを正とします。</li>
          </ul>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default StagingDomainFallback;
